// Pure client call-flow state machine (docs/design/05-uiux-spec.md §3, modules/04).
// Drives which screen is shown. Kept pure so the transitions are unit-testable.

export type CallScreen = "precall" | "calling" | "incoming" | "incall" | "ended";

export interface CallFlowState {
  screen: CallScreen;
  callId: number | null;
  peerId: string | null;
  mediaToken: string | null;
  remoteName: string | null;
  endReason: string | null;
}

export const initialCallFlow: CallFlowState = {
  screen: "precall",
  callId: null,
  peerId: null,
  mediaToken: null,
  remoteName: null,
  endReason: null
};

export type CallFlowAction =
  | { type: "placeCall"; callId: number; selfPeerId: string; remoteName?: string }
  | { type: "incomingCall"; callId: number; fromName?: string }
  | { type: "accepted"; callId: number; selfPeerId: string; mediaToken: string }
  | { type: "declined"; reason: string }
  | { type: "ended"; reason: string }
  | { type: "reset" };

export function callFlowReducer(state: CallFlowState, action: CallFlowAction): CallFlowState {
  switch (action.type) {
    case "placeCall":
      return {
        ...state,
        screen: "calling",
        callId: action.callId,
        peerId: action.selfPeerId,
        remoteName: action.remoteName ?? null,
        endReason: null
      };

    case "incomingCall":
      // Ignore a new incoming call while already busy (one call at a time on a connection).
      if (state.screen === "incall" || state.screen === "calling") return state;
      return { ...state, screen: "incoming", callId: action.callId, remoteName: action.fromName ?? null, endReason: null };

    case "accepted":
      // Applies to both sides: caller (was "calling") and callee (was "incoming").
      if (action.callId !== state.callId && state.screen !== "incoming") return state;
      return { ...state, screen: "incall", callId: action.callId, peerId: action.selfPeerId, mediaToken: action.mediaToken };

    case "declined":
      return { ...initialCallFlow, endReason: action.reason };

    case "ended":
      return { ...state, screen: "ended", endReason: action.reason };

    case "reset":
      return { ...initialCallFlow };

    default:
      return state;
  }
}
